import type { ChatEntry, ChatStatus, SessionMode } from "@foreman/protocol";
import type { ChatMessage } from "@foreman/link";

/** What the phone shows for one session's chat (and which way the session runs). */
export interface ChatView {
  mode: SessionMode;
  /** A hand-over between terminal and chat is under way. */
  switching: boolean;
  /** Why the last switch was refused or failed (cleared by the next one). */
  reason: string | null;
  /** The computer has sent the chat's state (until then only the mode is known). */
  loaded: boolean;
  /** Oldest first. */
  entries: ChatEntry[];
  /** Replies being written right now, in order. */
  streaming: { id: string; text: string }[];
  status: ChatStatus;
  detail: string | null;
  /** Where the conversation from before the chat ends (pass as `before` to read it); null: nothing before. */
  historyBefore: number | null;
  /** "agent": the agent can't ask in the chat; `note` says what happens instead. */
  approvals: "chat" | "agent";
  note: string | null;
  /** "Allow everything in this chat" is on: the computer answers every approval itself. */
  allowAll: boolean;
  /** The last thing the person tried that the computer refused ("Still replying…"). */
  error: string | null;
}

export const emptyChat = (mode: SessionMode = "terminal"): ChatView => ({
  mode,
  switching: false,
  reason: null,
  loaded: false,
  entries: [],
  streaming: [],
  status: "idle",
  detail: null,
  historyBefore: null,
  approvals: "chat",
  note: null,
  allowAll: false,
  error: null,
});

const MAX_ENTRIES = 400;

function upsert(entries: ChatEntry[], entry: ChatEntry): ChatEntry[] {
  const i = entries.findIndex((e) => e.id === entry.id && e.kind === entry.kind);
  if (i >= 0) {
    const next = entries.slice();
    next[i] = entry;
    return next;
  }
  const next = [...entries, entry];
  return next.length > MAX_ENTRIES ? next.slice(next.length - MAX_ENTRIES) : next;
}

/** Applies one message from the computer to a chat's view. Pure: returns a new view (or the same one if nothing changed). */
export function reduceChat(s: ChatView, m: ChatMessage): ChatView {
  switch (m.t) {
    case "session.mode":
      return { ...s, mode: m.mode, switching: m.switching, reason: m.reason ?? (m.switching ? null : s.reason && m.mode === s.mode ? s.reason : null), ...(m.mode === "chat" && s.mode !== "chat" ? { loaded: false } : {}) };
    case "chat.state":
      return {
        ...s,
        mode: "chat",
        loaded: true,
        entries: m.entries,
        streaming: m.streaming ? [m.streaming] : [],
        status: m.status,
        detail: m.detail ?? null,
        historyBefore: m.historyBefore,
        approvals: m.approvals,
        note: m.note ?? null,
        allowAll: m.allowAll ?? false,
      };
    case "chat.allowAll":
      return { ...s, allowAll: m.on };
    case "chat.delta": {
      const i = s.streaming.findIndex((x) => x.id === m.id);
      const streaming = i >= 0 ? s.streaming.map((x, j) => (j === i ? { id: x.id, text: x.text + m.text } : x)) : [...s.streaming, { id: m.id, text: m.text }];
      return { ...s, streaming, error: null };
    }
    case "chat.message":
      return { ...s, entries: upsert(s.entries, m.entry), streaming: s.streaming.filter((x) => x.id !== m.entry.id), ...(m.entry.kind === "text" && m.entry.role === "user" ? { error: null } : {}) };
    case "chat.tool":
    case "chat.approval":
      return { ...s, entries: upsert(s.entries, m.entry) };
    case "chat.status":
      return { ...s, status: m.status, detail: m.detail ?? null };
    case "chat.result":
      return { ...s, streaming: [], status: s.status === "error" ? s.status : "idle" };
  }
}

/** Busy: the agent is on a turn (or starting); the input waits, Stop shows. */
export function chatBusy(v: ChatView): boolean {
  return v.status === "thinking" || v.status === "tool" || v.status === "starting" || v.streaming.length > 0 || v.entries.some((e) => e.kind === "approval" && e.state === "open");
}

/** One session's chat as a small observable store. */
export class ChatStore {
  private view: ChatView;
  private readonly listeners = new Set<() => void>();

  constructor(mode: SessionMode = "terminal") {
    this.view = emptyChat(mode);
  }

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  getSnapshot = (): ChatView => this.view;

  apply(m: ChatMessage): void {
    this.set(reduceChat(this.view, m));
  }

  patch(p: Partial<ChatView>): void {
    this.set({ ...this.view, ...p });
  }

  private set(next: ChatView): void {
    if (next === this.view) return;
    this.view = next;
    for (const l of [...this.listeners]) l();
  }
}
